import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OverlayService, DialogDefinition } from '../../../services/overlay.service';
import { SapiService } from '../../../services/sapi.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { NgForm } from '@angular/forms';
import { Location } from '@angular/common';
import { ToCodePipe } from '../../../pipes/toCode.pipe';
import { Subscription, Subject } from 'rxjs';
import { BbSelectOption } from '../../../components/shared/bb-select/bb-select.component';
import { bbDeepCopy } from '../../../shared/utils/bbDeepCopy.util';
import { PublicationService } from '../../../services/publication.service';

export interface CustomFieldOption {
	label: string;
	value: string;
}

@Component({
	selector: 'custom-field-details',
	templateUrl: './custom-field-details.component.html',
	styleUrls: ['./custom-field-details.component.scss']
})

export class CustomFieldDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('formRef', { static: true }) form: NgForm;
	saveCustomFieldObservable: Subject<any>;
	subscriptions: Subscription = new Subscription();
	customField: any = {};
	customFieldCopy: any;
	customFieldName: string;
	customFieldTypes: any[];
	allCustomFields: any;
	allCustomFieldsCopy: any;
	contentOptions: BbSelectOption[];
	contentUseTypeOptions: any[];
	contentSubTypeOptions: any[];
	putRight: boolean;
	fieldsToSave: any = {};
	fieldType: string;
	entityConfiguration: any;
	isNew = false;
	uniqueCode = true;

	currentCode: string;

	dialog: DialogDefinition;

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		protected customfieldsService: CustomFieldsService,
		protected authenticationService: AuthenticationService,
		protected publicationService: PublicationService,
		protected overlayService: OverlayService,
		protected router: Router,
		protected sapiService: SapiService,
		protected translateService: TranslateService,
		protected toCodePipe: ToCodePipe) {

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		this.saveCustomFieldObservable = new Subject<any>();

		this.subscriptions.add(this.route.params.subscribe((params: Params) => {
			this.customFieldName = params.name;
		}));

		this.allCustomFields = this.customfieldsService.getCustomFields('mediaclip');

		this.customFieldTypes = [
			{
				label: 'textfield',
				itype: 'string',
				type: 'string'
			},
			{
				label: 'number',
				itype: 'number',
				type: 'int'
			},
			{
				label: 'textarea',
				itype: 'text',
				type: 'text'
			},
			{
				label: 'richtext',
				itype: 'richtext',
				type: 'text'
			},
			{
				label: 'checkbox',
				itype: 'checkbox',
				type: 'boolean'
			},
			{
				label: 'list',
				itype: 'list',
				type: 'multistring'
			},
			{
				label: 'relation',
				itype: 'relationlist',
				type: 'relationlist'
			},
			{
				label: 'date',
				itype: 'date',
				type: 'date'
			},
			{
				label: 'color',
				itype: 'color',
				type: 'string'
			}
		];

		this.contentUseTypeOptions = [
			{
				label: 'EDITORIAL',
				value: 'editorial'
			},
			{
				label: 'CREATIVE',
				value: 'commercial'
			}
		];

		this.contentSubTypeOptions = [
			{
				label: 'VIDEO',
				value: 'video'
			},
			{
				label: 'AUDIO',
				value: 'audio'
			},
			{
				label: 'IMAGE',
				value: 'image'
			},
			{
				label: 'INTERACTIVE',
				value: 'interactive'
			},
			{
				label: 'TEXT',
				value: 'text'
			},
			{
				label: 'DOCUMENT',
				value: 'document'
			}
		];

		this.contentOptions = [
			{
				label: this.translateService.instant('MEDIACLIP'),
				value: 'mediaclip',
				default: true
			}
		];
	}

	ngOnInit() {
		this.publicationService.getData(true).subscribe();
		if (this.customFieldName) {
			this.router.navigate(['/publication/custom-field/mediaclip/' + this.customFieldName]);
		}
		if (this.customfieldsService.getCustomField('mediaclip', this.customFieldName)) {
			this.customField = bbDeepCopy(this.customfieldsService.getCustomField('mediaclip', this.customFieldName));
			this.currentCode = '' + this.customField.name;
		} else {
			this.isNew = true;
			this.customField = {};
			this.customField.label = '';
			this.customField.name = '';
			this.customField.type = 'string';
			this.customField.itype = 'string';
			this.customField.public = false;
			this.customField.visible = true;
			this.customField.priority = null;
			this.customField.allowDecimals = false;
		}

		this.customFieldCopy = bbDeepCopy(this.customField);
		this.allCustomFieldsCopy = bbDeepCopy(this.allCustomFields);
		this.form.form.markAsPristine();
		this.subscriptions.add(this.form.control.valueChanges.subscribe(() => {
			if (!(this.customField.public && ['string', 'boolean', 'int', 'float'].includes(this.customField.type))) {
				this.customField.includeInStats = false;
			}
		}));

		this.customField.allowDecimals = this.customField.type === 'float';
	}

	ngAfterViewInit() {
		this.form.form.markAsPristine();
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	save() {
		if (!this.form.form.valid) {
			this.overlayService.createToast(this.translateService.instant('ERROR_CUSTOM_FIELD'), 'warning');
			return;
		}

		if (!this.uniqueCode) {
			this.overlayService.createToast(this.translateService.instant('CODE_MUST_BE_UNIQUE'));
			return;
		}


		if (this.customField.itype === 'list' && !this.customField.extendable && this.customField.options !== undefined) {
			for (let i = 0; i < this.customField.options.length; i++) {
				if (this.customField.options[i].label === '' || this.customField.options[i].value === '') {
					this.customField.options.splice(i, 1);
				} else {
					// This is needed because the AngularJS customservice adds a $$hashkey, which cannot be saved
					delete this.customField.options[i].$$hashKey;
					delete this.customField.options[i].$$hashkey;
				}
			}
		}

		if (this.customField.type === 'int' && this.customField.allowDecimals) {
			this.customField.type = 'float';
		}

		if (this.customField.type === 'float' && !this.customField.allowDecimals) {
			this.customField.type = 'int';
		}

		this.overlayService.setLoading(true);
		this.sapiService.putUrl('/publication/customfields/mediaclip/' + this.customField.name, this.customField).subscribe((newField) => {
			this.overlayService.createToast(this.translateService.instant('ENTITY_SAVED', {
				entity: this.translateService.instant('CUSTOMFIELD').toLowerCase(),
				id: this.customField.label
			}), 'save');
			this.publicationService.getData(true).subscribe();
			setTimeout(() => {
				this.router.navigate(['/publication/custom-field/mediaclip/' + newField.name]);
			}, 250);
		},
		(err) => {
			this.overlayService.createToast(this.translateService.instant('ERROR_CUSTOM_FIELD'), 'warning');
		}
		);
		this.overlayService.setLoading(false);
		this.form.form.markAsPristine();
		this.isNew = false;
	}

	cancel() {
		this.dialog = null;
		this.uniqueCode = true;

		this.ngOnInit();
	}

	updateCode() {
		if (!this.isNew) {
			return;
		}

		this.customField.name = this.toCodePipe.transform(this.customField.label);
		this.onCodeChange();
	}

	onCodeInput(ev) {
		setTimeout(() => {
			this.customField.name = this.toCodePipe.transform(this.customField.name, true);
		});
	}

	showCodeChangeWarning() {
		if (this.isNew || this.customFieldCopy.name === this.customField.name) {
			return;
		}

		if (this.customFieldCopy.name !== this.customField.name) {
			this.onCodeChange();
		}

		if (this.currentCode !== this.customField.name) {

			if (!this.dialog) {

				this.dialog = {
					icon: 'pencil-square',
					title: this.translateService.instant('CUSTOM_FIELD'),
					contents: this.translateService.instant('CUSTOM_FIELD_CODE_CHANGE_SURE'),
					onConfirm: () => {
						this.currentCode = '' + this.customField.name;
					},
					onCancel: () => {
						this.cancel();
					}
				};
				this.overlayService.createDialog(this.dialog);
			}
		}
	}

	onCodeChange() {
		// check name uniqueness
		for (let i = 0; i < this.allCustomFieldsCopy.length; i++) {
			if (this.customField.name === this.allCustomFieldsCopy[i].name) {
				this.uniqueCode = false;
				return;
			} else {
				this.uniqueCode = true;
			}
		}
	}

	fieldTypeChanged(fieldType: any) {
		if (fieldType) {
			this.customField.itype = fieldType.itype;
			this.customField.type = fieldType.type;
		}
	}

	multipleRelationsChanged(typeValue: string) {
		if (typeValue === 'relationlist') {
			this.customField.itype = 'relationlist';
		} else {
			this.customField.itype = 'relation';
		}
	}

	hasUseType(useType: string) {
		let ret = false;
		if (this.customField && this.customField.contentusetypes && this.customField.contentusetypes.length) {
			for (let i = 0; i < this.customField.contentusetypes.length; i++) {
				if (useType.indexOf(this.customField.contentusetypes[i]) !== -1) {
					ret = true;
				}
			}
		}
		return ret;
	}

	useTypeChange(option: string, checked: boolean, index: number) {
		if (checked) {
			if (!this.customField.contentusetypes) {
				this.customField.contentusetypes = [];
			}
			this.customField.contentusetypes.push(option);
		} else {
			const itemIndex = this.customField.contentusetypes.indexOf(option);
			if (itemIndex > -1) {
				this.customField.contentusetypes.splice(itemIndex, 1);
			}
		}
		this.form.form.markAsDirty();
	}

	hasMediaType(mediaType: string) {
		let ret = false;
		if (this.customField && this.customField.contentsubtypes && this.customField.contentsubtypes.length) {
			for (let i = 0; i < this.customField.contentsubtypes.length; i++) {
				if (mediaType.indexOf(this.customField.contentsubtypes[i]) !== -1) {
					ret = true;
				}
			}
		}
		return ret;
	}

	mediaTypeChange(option: string, checked: boolean, index: number) {
		if (checked) {
			if (!this.customField.contentsubtypes) {
				this.customField.contentsubtypes = [];
			}
			this.customField.contentsubtypes.push(option);
		} else {
			const itemIndex = this.customField.contentsubtypes.indexOf(option);
			if (itemIndex > -1) {
				this.customField.contentsubtypes.splice(itemIndex, 1);
			}
		}
		this.form.form.markAsDirty();
	}
}
